import React, { useState, useEffect, useContext, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { Route } from 'react-router-dom';
import { AppContext } from '@/AppContext';
import SAInnerMenu from '@/components/SAInnerMenu/SAInnerMenu';
import AccountToolbar from './components/AccountToolbar/AccountToolbar';
import AccountsTable from './components/AccountsTable/AccountsTable';
import AccountAdd from './components/AccountAdd/AccountAdd';
import AccountEdit from './components/AccountEdit/AccountEdit';
import ChangeBalance from './components/ChangeBalance/ChangeBalance';
import ChangeServicePlan from './components/ChangeServicePlan/ChangeServicePlan';
import ApplyServicePack from './components/ApplyServicePack/ApplyServicePack';

const PREFIX = 'Account';

const classes = {
  root: `${PREFIX}-root`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.root}`]: {
    padding: theme.spacing(3),
  },
}));

const Account = props => {
  const appContext = useContext(AppContext);

  const [refreshCounter, setRefresh] = useState(0);
  const [accounts, setAccounts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    appContext.accountApi
      .listAccounts(page, rowsPerPage, debouncedSearchQuery || undefined)
      .then(({ data }) => {
        setAccounts(data.data || []);
        setTotalCount(data.totalCount || 0);
      })
      .catch(appContext.errorHandler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshCounter, page, rowsPerPage, debouncedSearchQuery]);

  const backAndRefresh = data => {
    appContext.history.push('/sa/account');
    if (data) {
      setRefresh(refreshCounter + 1);
    }
  };

  const handleDelete = accountId => {
    appContext.accountApi.deleteAllByAccount(accountId).then(() => {
      setAccounts(accounts => accounts.filter(a => a.accountId !== accountId));
    });
  };

  const handleLogin = accountId => {
    appContext.accountApi.loginIntoAccount(accountId).then(({ data }) => {
      appContext.login(data);
      appContext.history.push('/meetup');
    });
  };

  const handleSearchChange = query => {
    setSearchQuery(query);
    setPage(0); // Reset to first page when searching
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Root className={classes.root}>
      <SAInnerMenu />
      <AccountToolbar searchQuery={searchQuery} onSearchChange={handleSearchChange} />
      <AccountsTable
        accounts={accounts}
        handleDelete={handleDelete}
        handleLogin={handleLogin}
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <Route path={'/sa/account/add'} exact>
        <AccountAdd goBack={backAndRefresh} />
      </Route>
      <Route path={'/sa/account/id/:accountId'} exact>
        <AccountEdit goBack={backAndRefresh} />
      </Route>
      <Route path={'/sa/account/id/:accountId/payment'} exact>
        <ChangeBalance goBack={backAndRefresh} />
      </Route>
      <Route path={'/sa/account/id/:accountId/tariff'} exact>
        <ChangeServicePlan goBack={backAndRefresh} />
      </Route>
      <Route path={'/sa/account/id/:accountId/pack'} exact>
        <ApplyServicePack goBack={backAndRefresh} />
      </Route>
    </Root>
  );
};

export default Account;
